<template>
  <div>
    <v-dialog
      v-model="showMenuEmbed"
      max-width="640px"
      max-height="640px"
      :persistent="false"
      content-class="embed-modal"
      :fullscreen="isMobileBreakpoint"
      style="max-height: 89%"
    >
      <!-- has content -->
      <v-card
        v-if="!fetching"
        class="pt-7 pb-5 px-8"
        :style="{
          minHeight: isMobileBreakpoint ? '' : '512px',
          borderRadius: isMobileBreakpoint ? '8px' : '0',
        }"
      >
        <v-row :class="{ 'pa-4': isMobileBreakpoint }" no-gutters>
          <div style="width: 100%">
            <v-row no-gutters align="center">
              <v-col>
                <span class="body-bold-medium mb-2">
                  {{
                    fromVideoListing
                      ? $t('message.embedModal.codeEmbed')
                      : $t('message.embedModal.shareVideo')
                  }}
                </span>
              </v-col>
              <v-col class="d-flex justify-end">
                <v-icon
                  @click="closeModal"
                  class="close-icon"
                  size="24"
                  color="grey"
                  style="cursor: pointer"
                >
                  mdi-close
                </v-icon>
              </v-col>
            </v-row>
            <span
              v-if="!fromVideoListing"
              class="body-regular-smaller descriptionText mb-6"
            >
              {{ $t('message.embedModal.bestWayToShare') }}
              <a target="_blank" type="secondary" :href="embedArticle">
                <span>
                  {{ $t('message.embedModal.knowMore') }}
                </span>
                <v-icon size="12">mdi-open-in-new</v-icon>
              </a>
            </span>
          </div>
          <v-tabs
            v-if="!fromVideoListing"
            v-model="activeTab"
            class="custom-tabs mt-6"
            style="padding: 0px !important; font-size: 14px !important"
          >
            <v-tab v-show="!(type === 'comparison' || isExternalSourceVideo)">{{
              $t('message.embedModal.share')
            }}</v-tab>
            <v-tab>{{ $t('message.embedModal.embed') }}</v-tab>
            <v-tab v-show="type !== 'comparison'"
              >⚡ {{ $t('message.embedModal.performance') }}</v-tab
            >
            <v-tab v-show="!(type === 'comparison' || isExternalSourceVideo)">
              HLS</v-tab
            >
          </v-tabs>

          <v-row no-gutters v-if="activeTab === 1">
            <v-col cols="12" class="pt-2 selectPlatforms">
              <div v-if="!fromVideoListing" style="display: grid" class="mb-3">
                <span class="body-regular-smaller sectionTitles mb-1">{{
                  $t('message.embedModal.copyEmbed')
                }}</span>
                <span class="body-regular-smaller descriptionText">{{
                  $t('message.embedModal.useCode')
                }}</span>
              </div>
              <div v-else class="body-regular-smaller textType4--text">
                {{ $t('message.embedModal.code') }}
              </div>
              <BaseAlert
                v-if="planInfo.subscription.status === 'trialing'"
                class="mb-8"
                type="warning"
                :title="$t('message.embedModal.attention')"
                :description="$t('message.embedModal.trialWarningTraffic')"
              >
                <base-btn
                  @click="openTrialArticle"
                  size="small"
                  type="neutral"
                  style="background: white; color: #3f404c"
                  >{{ $t('message.embedModal.knowMore') }}</base-btn
                ></BaseAlert
              >
              <!-- platforms -->
              <v-select
                class="mt-8"
                v-if="!isExternalSourceVideo"
                v-model="embedSelected"
                :items="embedOptions"
                item-text="text"
                item-value="value"
                dense
                hide-details
                :label="
                  $t('message.pages.videoEditModal.embedConfiguration.platform')
                "
                outlined
                no-data-text=""
                :menu-props="{ offsetY: true }"
                @change="
                  handleEmbedSelection(embedSelected)
                  changesInEmbed()
                "
              />
            </v-col>

            <!-- embed -->
            <v-col cols="12" class="py-0 embed-textarea">
              <v-col
                v-if="!embedView && embedSelected === 'elementor'"
                readonly
                hide-details
                dense
                no-resize
                outlined
                class="wrapText embedInput mt-3 p-2"
                style="
                  background: rgba(255, 255, 255, 0.5);
                  position: absolute;
                  z-index: 1;
                  width: 575px;
                  backdrop-filter: blur(2px);
                  border-radius: 5px;
                  border: 1px solid #8b8b8b;
                  height: 160px;
                "
              >
                <span class="d-flex align-center justify-center mb-2 mt-5 mx-2">
                  <h5 align="center" style="font-size: 16px; color: #484848">
                    {{ $t('message.embedModal.embedElementorText') }}
                  </h5>
                </span>
                <v-col class="d-flex justify-space-around">
                  <v-btn
                    outlined
                    color="primary"
                    small
                    @click="embedView = !embedView"
                    style="background: #fff"
                  >
                    {{ $t('message.embedModal.openEmbedElementor') }}
                  </v-btn>
                  <v-btn
                    color="primary"
                    class="white--text"
                    :href="elementorArticle"
                    target="_blank"
                    small
                    @click="$emit('showEmbed', false)"
                  >
                    {{ $t('message.embedModal.goToPlugin') }}
                  </v-btn>
                </v-col>
              </v-col>
              <v-textarea
                class="mt-4 embedInput"
                v-if="!fetching"
                v-model="textareaContent"
                outlined
                hide-details
                dense
                readonly
                no-resize
                color="primary"
                style="font-size: 14px"
                :class="{ 'v-input--is-focused': isFocused }"
                :rows="textareaRows"
                @focus="$event.target.select()"
              >
                <template v-slot:append>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        size="16"
                        class="mt-3 pandaCopy"
                        @mousedown.stop="focusIcon"
                        @click.stop="handleShowLink(textareaContent)"
                      >
                        panda-copy
                      </v-icon>
                    </template>
                    <span>
                      {{
                        $t(
                          `message.pages.videoEditModal.${
                            isCopied ? 'copied' : 'copyToClipboard'
                          }`
                        )
                      }}
                    </span>
                  </v-tooltip>
                </template>
              </v-textarea>
            </v-col>

            <template v-if="showConfigs">
              <template
                v-if="
                  isExternalSourceVideo &&
                  (video.video_source === 'youtube' ||
                    video.video_source === 'vimeo')
                "
              >
                <v-col cols="12" class="d-flex mt-4">
                  <v-switch
                    v-model="showYoutubeControls"
                    class="my-0 py-0"
                    hide-details
                  />
                  <span>{{
                    $t('message.embedModal.hideExternalControls')
                  }}</span>
                </v-col>
              </template>
              <!-- startWithin -->
              <v-col
                v-if="
                  !(isExternalSourceVideo && openFromVideoList) &&
                  (video.status === 'CONVERTED' ||
                    (video.status === 'finished_imported' && video.video_id))
                "
                cols="12"
                class="mt-2 mb-5"
              >
                <v-row no-gutters class="justify-space-between">
                  <v-col cols="3" class="d-flex align-center mr-2">
                    <span class="body-regular-smaller embedLabelOptions">
                      <!-- {{
                    $t(
                      'message.pages.videoEditModal.embedConfiguration.preloadEmbed'
                    )
                  }} -->
                      {{ $t('message.embedModal.startsIn') }}
                    </span>
                  </v-col>
                  <v-col cols="3" class="timePicker d-flex justify-end">
                    <AntTimePicker
                      :startVal="startTime"
                      :timeVideo="timeVideo"
                      :disabled="!embedVideo.startTimeCheckbox"
                      @changedTime="handleChangeStartTime"
                    />
                  </v-col>
                </v-row>
              </v-col>

              <template v-if="!isExternalSourceVideo">
                <!-- preload -->
                <v-col
                  cols="12"
                  class="d-flex mb-2 align-center justify-space-between"
                >
                  <span class="body-regular-smaller embedLabelOptions">
                    <!-- {{
                    $t(
                      'message.pages.videoEditModal.embedConfiguration.preloadEmbed'
                    )
                  }} -->
                    {{ $t('message.embedModal.preloadStart') }}
                  </span>
                  <v-switch
                    v-model="preloadEmbed"
                    class="my-0 py-0"
                    hide-details
                    @change="changesInEmbed()"
                  />
                  <!-- <v-btn
                    icon
                    color="primary"
                    :href="$t('message.embedModal.preloadEmbedHelpDesk')"
                    target="_blank"
                  >
                    <v-icon>mdi-help-circle</v-icon>
                  </v-btn> -->
                </v-col>
                <v-col cols="12" class="mt-6">
                  <v-divider />
                </v-col>

                <v-col cols="12" class="mb-2 mt-5">
                  <span class="body-regular-smaller sectionTitles">
                    <!-- {{ $t('message.embedModal.embedOptionsTitle') }} -->
                    {{ $t('message.embedModal.screenSize') }}
                  </span>
                </v-col>

                <!-- responsive + dimension -->
                <div
                  style="width: 100%"
                  class="d-flex mt-1 justify-space-between embedLabelOptions"
                >
                  <span> {{ $t('message.embedModal.responsive') }} </span>
                  <v-switch
                    @change="changesInEmbed()"
                    v-model="switchModel"
                  ></v-switch>
                </div>
                <v-row no-gutters>
                  <!-- dimension -->
                  <div style="width: 100%">
                    <v-row
                      no-gutters
                      class="d-flex justify-space-between align-center embed-textarea"
                    >
                      <span class="embedLabelOptions">{{
                        $t('message.embedModal.screenHeight')
                      }}</span>
                      <v-responsive
                        :max-width="embedVideo.width.length === 4 ? 60 : 52"
                      >
                        <v-textarea
                          v-model="embedVideo.width"
                          class="sizeInput"
                          type="number"
                          @keypress="allowOnlyNumbers($event)"
                          maxlength="4"
                          @change="changesInEmbed()"
                          color="primary"
                          no-resize
                          rows="1"
                          outlined
                          dense
                          hide-details
                          :disabled="videoCustomSize || switchModel"
                        >
                          <template v-slot:append>
                            <span class="appendPx">px</span>
                          </template>
                        </v-textarea>
                      </v-responsive>
                    </v-row>
                    <v-row
                      no-gutters
                      class="d-flex mt-4 justify-space-between embed-textarea"
                    >
                      <span class="embedLabelOptions">{{
                        $t('message.embedModal.screenWidth')
                      }}</span>
                      <v-responsive
                        :max-width="embedVideo.height.length === 4 ? 60 : 52"
                      >
                        <v-textarea
                          v-model="embedVideo.height"
                          type="number"
                          @change="changesInEmbed()"
                          @keypress="allowOnlyNumbers($event)"
                          maxlength="4"
                          rows="1"
                          no-resize
                          class="sizeInput"
                          color="primary"
                          outlined
                          dense
                          hide-details
                          :disabled="videoCustomSize || switchModel"
                        >
                          <template v-slot:append>
                            <span class="appendPx">px</span>
                          </template>
                        </v-textarea>
                      </v-responsive>
                    </v-row>
                  </div>
                </v-row>
              </template>
              <template v-else>
                <!-- responsive + dimension -->
                <v-col cols="12" class="mt-1 mb-6">
                  <v-row no-gutters>
                    <!-- responsive -->
                    <v-col cols="5" class="d-flex align-self-center">
                      <v-radio-group
                        v-model="radioActive"
                        row
                        mandatory
                        hide-details
                        class="my-0 py-0"
                      >
                        <v-radio
                          :label="
                            $t(
                              'message.pages.videoEditModal.embedConfiguration.responsive'
                            )
                          "
                          :value="1"
                          :disabled="
                            radioActive != activeRadio && isRestrictEmbed
                          "
                          @click="videoCustomSize = true"
                        />
                        <v-radio
                          :label="
                            $t(
                              'message.pages.videoEditModal.embedConfiguration.fixed'
                            )
                          "
                          :value="0"
                          :disabled="
                            radioActive === activeRadio && isRestrictEmbed
                          "
                          @click="videoCustomSize = false"
                        />
                      </v-radio-group>
                    </v-col>

                    <!-- dimension -->
                    <v-col>
                      <v-row>
                        <v-col>
                          <v-text-field
                            v-model="embedVideo.width"
                            type="number"
                            color="primary"
                            :label="
                              $t(
                                'message.pages.videoEditModal.embedConfiguration.width'
                              ) + ' (px)'
                            "
                            outlined
                            dense
                            hide-details
                            :disabled="videoCustomSize"
                          />
                        </v-col>
                        <v-col>
                          <v-text-field
                            v-model="embedVideo.height"
                            type="number"
                            color="primary"
                            :label="
                              $t(
                                'message.pages.videoEditModal.embedConfiguration.height'
                              ) + ' (px)'
                            "
                            outlined
                            dense
                            hide-details
                            :disabled="videoCustomSize"
                          />
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-col>
              </template>
            </template>
            <template v-if="!isExternalSourceVideo">
              <!-- fullscreen -->
              <template v-if="showFullscreen">
                <v-col cols="12" class="mb-2">
                  <v-divider class="my-4"></v-divider>
                  <span class="body-regular-smaller sectionTitles">
                    <!-- {{ $t('message.embedModal.embedOptionsTitle') }} -->
                    {{ $t('message.embedModal.fullscreen') }}
                  </span>
                </v-col>
                <v-col cols="12" class="d-flex mb-4 justify-space-between">
                  <span class="body-regular-smaller embedLabelOptions">
                    <!-- {{
                    $t('message.embedModal.playOpensFullscreenNative')
                  }} -->
                    {{ $t('message.embedModal.nativePlayer') }}
                  </span>
                  <v-switch
                    v-model="playOpensFullscreen.iosFakeFullscreen"
                    class="my-0 py-0"
                    hide-details
                    @change="handleIosFakeFullscreen"
                  />
                </v-col>
              </template>
              <template v-if="showFullscreen">
                <v-col cols="12" class="d-flex mb-4 justify-space-between">
                  <span class="body-regular-smaller embedLabelOptions">{{
                    $t('message.embedModal.playOpensFullscreenNativeOnPlay')
                  }}</span>
                  <v-switch
                    v-model="playOpensFullscreen.native"
                    class="my-0 py-0"
                    hide-details
                    @change="handleFullscreenNative"
                  />
                </v-col>
                <template v-if="embedSelected === 'general'">
                  <v-col cols="12" class="d-flex mb-4 justify-space-between">
                    <span class="body-regular-smaller embedLabelOptions">{{
                      $t('message.embedModal.playOpensFullscreen')
                    }}</span>
                    <v-switch
                      v-model="playOpensFullscreen.script"
                      class="my-0 py-0"
                      hide-details
                      @change="handleFullscreen"
                    />
                  </v-col>
                  <BaseAlert
                    v-if="
                      !playOpensFullscreen.iosFakeFullscreen ||
                      playOpensFullscreen.native ||
                      playOpensFullscreen.script
                    "
                    class="mb-8"
                    type="warning"
                    :title="$t('message.embedModal.compatibilityWarning')"
                    :description="$t('message.embedModal.compatibilityMessage')"
                  />
                </template>
              </template>

              <template v-if="!isExternalSourceVideo">
                <!-- pip scrollfollow -->
                <template v-if="showFullscreen">
                  <v-col cols="12" class="mb-2">
                    <v-divider class="my-4"></v-divider>
                    <span class="body-regular-smaller sectionTitles">
                      {{ $t('message.embedModal.advanced') }}
                    </span>
                  </v-col>
                  <v-col cols="12" class="d-flex mb-4 justify-space-between">
                    <v-row no-gutters>
                      <span class="body-regular-smaller embedLabelOptions">
                        {{ $t('message.embedModal.pipScrollFollow') }}
                      </span>
                      <span
                        class="body-regular-smaller embedLabelOptions"
                        style="color: var(--neutral-40-light-light)"
                      >
                        {{ $t('message.embedModal.pipScrollFollowText') }}
                      </span>
                    </v-row>
                    <v-switch
                      v-model="pipScrollFollow"
                      class="my-0 py-0"
                      hide-details
                      @change="handlePipScrollFollow"
                    />
                  </v-col>
                  <BaseAlert
                    v-if="pipScrollFollow"
                    class="mb-8"
                    type="warning"
                    :title="$t('message.embedModal.compatibilityWarning')"
                    :description="$t('message.embedModal.compatibilityMessage')"
                  />
                </template>
              </template>

              <!-- is code -->
              <template v-if="codeEmbed">
                <v-col cols="12" class="mt-5 mb-4">
                  <v-divider />
                </v-col>
                <v-col
                  cols="12"
                  v-html="
                    $t('message.embedModal.codeMessage', {
                      embedSelected: embedTrademark,
                    })
                  "
                />
              </template>

              <!-- is hls -->
              <template v-if="hlsEmbed">
                <v-col cols="12" class="mt-5 mb-4">
                  <v-divider />
                </v-col>
                <span class="body-regular-smaller descriptionText">
                  {{
                    $t('message.embedModal.hlsMessage', {
                      embedSelected: embedTrademark,
                    })
                  }}
                  <a
                    type="secondary"
                    target="_blank"
                    :class="
                      $t('message.embedModal.embedIntegrationLink', {
                        embedSelected: embedClassHelp,
                      })
                    "
                    :href="embedLinkHelp"
                  >
                    <span>
                      {{ $t('message.embedModal.knowMoreInArticle') }}
                    </span>

                    <v-icon size="12">mdi-open-in-new</v-icon>
                  </a>
                </span>
              </template>

              <v-col
                cols="12"
                class="mt-1"
                :class="
                  $t('message.embedModal.embedIntegrationLink', {
                    embedSelected: embedClassHelp,
                  })
                "
              >
                <span
                  class="body-regular-smaller descriptionText"
                  v-if="!hlsEmbed"
                >
                  {{
                    $t('message.embedModal.howIntegrateEmbed', {
                      embedSelected: embedTrademark,
                    })
                  }}
                  <a type="secondary" target="_blank" :href="embedLinkHelp">
                    <!-- {{ $t('message.embedModal.pleaseRead') }} -->
                    <span>
                      {{ $t('message.embedModal.knowMoreInArticle') }}
                    </span>
                    <v-icon size="12">mdi-open-in-new</v-icon></a
                  >
                </span>
              </v-col>

              <!-- is elementor -->
              <template v-if="embedSelected === 'elementor' && embedView">
                <v-col cols="12" class="mb-4">
                  <v-divider />
                </v-col>
                <v-col class="d-flex align-center" cols="12">
                  <p
                    class="ma-0"
                    v-html="$t('message.embedModal.elementorPlugin')"
                  ></p>
                </v-col>
              </template>
            </template>
          </v-row>

          <!-- share tab -->
          <div
            style="width: 100%"
            v-if="activeTab === 0"
            class="embed-textarea"
          >
            <!-- <BaseAlert
              v-if="userLibrary.block_none_refer"
              class="mb-8"
              type="warning"
              :title="$t('message.pages.folderSettings.alert')"
              :description="$t('message.pages.trash.infoText')"
            /> -->
            <BaseAlert
              v-if="userLibrary.block_none_refer"
              class="mb-8"
              type="warning"
              :title="$t('message.embedModal.domainWarning')"
              :description="$t('message.embedModal.domainMessage')"
            >
              <base-btn
                @click="goToDomainsPage"
                size="small"
                type="neutral"
                style="background: white; color: #3f404c"
                >{{ $t('message.embedModal.domainBtn') }}</base-btn
              ></BaseAlert
            >
            <div style="display: grid" class="mb-3 mt-2">
              <span class="body-regular-smaller sectionTitles mb-1">{{
                $t('message.embedModal.publicLink')
              }}</span>
              <span class="body-regular-smaller descriptionText">{{
                $t('message.embedModal.publicLinkMessage')
              }}</span>
            </div>

            <v-textarea
              no-resize
              readonly
              rows="2"
              class="embedInput"
              v-model="video.video_player"
            >
              <template v-slot:append>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      @click="handleShowLink(video.video_player)"
                      v-bind="attrs"
                      v-on="on"
                      size="16"
                      class="mt-3 pandaCopy"
                    >
                      panda-copy
                    </v-icon>
                  </template>
                  <span>
                    {{
                      $t(
                        `message.pages.videoEditModal.${
                          isCopied ? 'copied' : 'copyToClipboard'
                        }`
                      )
                    }}
                  </span>
                </v-tooltip>
              </template>
            </v-textarea>
          </div>

          <!-- Conteúdo da aba Performance -->
          <div v-if="activeTab === 2" class="embed-textarea">
            <div style="display: grid" class="mb-3 mt-2">
              <span class="body-regular-smaller sectionTitles mb-1">{{
                $t('message.embedModal.performanceCode')
              }}</span>
              <span
                class="body-regular-smaller descriptionText mb-1"
                v-html="$t('message.embedModal.salesPageMessage')"
              ></span>
            </div>

            <v-textarea
              hide-details
              readonly
              no-resize
              rows="5"
              v-model="textMetadata"
              class="mb-1"
              :class="{ 'v-input--is-focused': isFocused }"
            >
              <template v-slot:append>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      @click.stop="handleShowLink(textMetadata)"
                      v-bind="attrs"
                      v-on="on"
                      @mousedown.stop="focusIcon"
                      size="16"
                      class="mt-3 pandaCopy"
                    >
                      panda-copy
                    </v-icon>
                  </template>
                  <span>
                    {{
                      $t(
                        `message.pages.videoEditModal.${
                          isCopied ? 'copied' : 'copyToClipboard'
                        }`
                      )
                    }}
                  </span>
                </v-tooltip>
              </template>
            </v-textarea>
            <span class="body-regular-smaller descriptionText">
              {{ $t('message.embedModal.knowMoreHow') }}
              <a type="secondary" target="_blank" :href="performanceArticle">
                <span>
                  {{ $t('message.embedModal.improvePerformance') }}
                </span>
                <v-icon size="12">mdi-open-in-new</v-icon>
              </a>
            </span>
          </div>

          <div
            style="width: 100%"
            v-if="activeTab === 3"
            class="embed-textarea"
          >
            <div style="display: grid" class="mb-3 mt-2">
              <span class="body-regular-smaller sectionTitles mb-1">{{
                $t('message.embedModal.copyHLSCode')
              }}</span>
              <span class="body-regular-smaller descriptionText">
                {{ $t('message.embedModal.useHLSCode') }}
              </span>
            </div>
            <v-textarea
              class="mb-1"
              hide-details
              readonly
              no-resize
              rows="2"
              v-model="video.video_hls"
            >
              <template v-slot:append>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      @click="handleShowLink(video.video_hls)"
                      v-bind="attrs"
                      v-on="on"
                      size="16"
                      class="mt-3 pandaCopy"
                    >
                      panda-copy
                    </v-icon>
                  </template>
                  <span>
                    {{
                      $t(
                        `message.pages.videoEditModal.${
                          isCopied ? 'copied' : 'copyToClipboard'
                        }`
                      )
                    }}
                  </span>
                </v-tooltip>
              </template>
            </v-textarea>
            <span class="body-regular-smaller descriptionText">
              {{ $t('message.embedModal.knowMoreAbout') }}
              <a type="secondary" target="_blank" :href="privacyArticle">
                <span>
                  {{ $t('message.embedModal.privacyConfigs') }}
                </span>

                <v-icon size="12">mdi-open-in-new</v-icon>
              </a>
            </span>
          </div>
        </v-row>
      </v-card>

      <!-- is loading -->
      <v-card v-else>
        <v-row no-gutters style="height: 440px; max-height: 440px">
          <v-col
            class="d-flex align-center justify-center"
            style="height: 100%"
          >
            <loading-logo :value="200" />
          </v-col>
        </v-row>
      </v-card>
      <div class="py-6 pr-7 actionsBtns">
        <v-spacer></v-spacer>
        <base-btn @click="closeModal" class="mr-2" type="tertiary">
          <span>{{ $t('message.embedModal.cancel') }}</span>
        </base-btn>
        <base-btn @click="copyContent" type="primary">
          <span>{{ $t('message.embedModal.copy') }}</span>
        </base-btn>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { mask } from 'vue-the-mask'
import { mapMutations, mapState } from 'vuex'
import BaseAlert from '@/components/BaseAlert.vue'

import LoadingLogo from '@/components/LoadingLogo.vue'
import AntTimePicker from '@/components/AntTimePicker.vue'

import embedSelection from '@/utils/embedSelection.mixin.js'
import embedCode from '@/utils/embedCode.mixin.js'
import formatTimeMixin from '@/utils/formatTime.mixins.js'
import referApi from '@/services/referApi.js'

import { embedOptions as platformsOfEmbed } from '@/utils/embedOptions.js'
import videosApi from '@/services/videosApi'
import Storage from '@/utils/Storage'
import i18n from '@/translations/lang'

export default {
  name: 'embedVideo',
  directives: { mask },
  components: { LoadingLogo, AntTimePicker, BaseAlert },
  props: {
    onlyPageOptions: {
      type: Boolean,
      default: false,
    },
    fromVideoListing: {
      type: Boolean,
      default: false,
    },
    defaultTab: {
      type: Number,
      default: 1,
    },
    isAbTest: {
      type: Boolean,
      default: false,
    },
    additionalTextAreaContent: {
      default: '',
      type: String,
    },
    videoId: {
      default: undefined,
    },
    showMenuEmbed: {
      default: false,
    },
    timeVideo: {
      default: 0,
    },
    startTime: {
      default: '00:00:00',
    },
    fetching: {
      default: true,
    },
    video: {
      type: Object,
    },
    livePlayer: {
      required: false,
    },
    isPlayerLiveState: {
      required: false,
    },
    liveInfo: {
      required: false,
    },
    type: {
      default: false,
    },
    isExternalSourceVideo: {
      default: false,
      type: Boolean,
    },
    openFromVideoList: {
      default: false,
      type: Boolean,
    },
  },
  mixins: [embedSelection, embedCode, formatTimeMixin],
  data: () => ({
    showYoutubeControls: true,
    hasChanges: false,
    privacyArticle: 'https://www.pandavideo.com.br/privacy',
    activeRadio: 1,
    embedTrademark: '',
    embedSelected: '',
    hlsEmbed: false,
    codeEmbed: false,
    activeTab: 0,
    srcEmbed: false,
    isExternalIdEmbed: false,
    showMenu: false,
    showMenuLink: false,
    menuItem: {},
    embedVideo: {
      width: 720,
      height: 360,
      startTimeCheckbox: true,
      startTime: '',
    },
    currentTime: 0,
    valid: false,
    isLoading: false,
    oldVideo: {},
    rules: {
      required: (v) => (v && !!v) || i18n.t('message.requiredField'),
      maxLength: (v) =>
        (v && v.length <= 255) || i18n.t('message.maximumLengthVideo'),
      minLength: (v) => (v && v.length > 2) || i18n.t('message.minimumLength'),
    },
    errors: {
      failedToUpdateVideo: false,
      videoDoesNotExist: false,
    },
    networkErrorMsg: '',
    unknownError: '',
    videoCustomSize: false,
    isCopied: false,
    value: 0,
    activeInput: null,
    db: null,
    changeDomainVerification: false,
    userLibrary: {},
    isFocused: false,
    radioActive: 0,
    item: '',
    thumbnailUrl: null,
    date: '',
    isChangingPlatform: false,
    downloadServer: '',
    signedToken: '',
    playOpensFullscreen: {
      native: false,
      script: false,
      iosFakeFullscreen: true,
    },
    pipScrollFollow: false,
    embedView: false,
    preloadEmbed: true,
    modalElement: undefined,
  }),
  computed: {
    ...mapState('user', {
      planInfo: 'userPlanInfo',
      isMobileBreakpoint: 'isMobileBreakpoint',
    }),
    textMetadata() {
      if (this.isExternalSourceVideo) {
        return `<link rel="preload" href="https://player.pandavideo.com.br/player.external.js" as="script">
<link rel="preload" href="https://config.tv.pandavideo.com.br/${this.video.pullzone_name}/${this.video.video_external_id}.json" as="fetch">
<link rel="preload" href="https://config.tv.pandavideo.com.br/${this.video.pullzone_name}/config.json" as="fetch">`
      }
      return `<link rel="preload" href="https://player-${this.video.pullzone_name}.tv.pandavideo.com.br/embed/css/styles.css" as="style">
<link rel="prerender" href="https://player-${this.video.pullzone_name}.tv.pandavideo.com.br/embed/?v=${this.video.video_external_id}">
<link rel="preload" href="https://player-${this.video.pullzone_name}.tv.pandavideo.com.br/embed/js/hls.js" as="script">
<link rel="preload" href="https://player-${this.video.pullzone_name}.tv.pandavideo.com.br/embed/js/plyr.polyfilled.min.js" as="script">
<link rel="preload" href="https://config.tv.pandavideo.com.br/${this.video.pullzone_name}/${this.video.video_external_id}.json" as="fetch">
<link rel="preload" href="https://config.tv.pandavideo.com.br/${this.video.pullzone_name}/config.json" as="fetch">
<link rel="preload" href="https://b-${this.video.pullzone_name}.tv.pandavideo.com.br/${this.video.video_external_id}/playlist.m3u8" as="fetch">
<link rel="dns-prefetch" href="https://b-${this.video.pullzone_name}.tv.pandavideo.com.br">
<link rel="dns-prefetch" href="https://player-${this.video.pullzone_name}.tv.pandavideo.com.br">
<link rel="dns-prefetch" href="https://${this.video.pullzone_name}.b-cdn.net">`
    },
    switchModel: {
      get() {
        return this.radioActive === 1
      },
      set(value) {
        this.radioActive = value ? 1 : 0
      },
    },
    performanceArticle() {
      if (this.locale === 'en') {
        return 'https://help.pandavideo.com/en-us/article/how-can-i-use-performance-code-fivofh/'
      }
      if (this.locale === 'es') {
        return 'https://help.pandavideo.com/es/article/como-funciona-el-codigo-de-rendimiento-lnhtty/'
      }
      return 'https://help.pandavideo.com/pt-br/article/como-funciona-o-codigo-de-performance-1omrv62/'
    },
    embedArticle() {
      if (this.locale === 'en') {
        return 'https://help.pandavideo.com/en-us/article/how-to-share-and-embed-your-videos-on0ugb/'
      }
      if (this.locale === 'es') {
        return 'https://help.pandavideo.com/es/article/como-compartir-e-incorporar-tus-videos-1t4m0sh/'
      }
      return 'https://help.pandavideo.com/pt-br/article/como-compartilhar-e-incorporar-seus-videos-1rt8jt0/'
    },
    elementorArticle() {
      if (this.locale === 'en') {
        return 'https://help.pandavideo.com/en-us/article/how-to-install-panda-video-plugin-in-elementor-1ac6k71/'
      }
      return 'https://help.pandavideo.com/pt-br/article/como-instalar-o-plugin-da-panda-video-no-elementor-kxiv0o/'
    },
    trialArticle() {
      if (this.locale === 'en') {
        return 'https://help.pandavideo.com/en-us/article/traffic-limit-during-the-trial-period-lchlqh/'
      }
      return 'https://help.pandavideo.com/pt-br/article/limite-de-banda-durante-o-periodo-trial-gpna2k/'
    },
    locale() {
      return this.$i18next.resolvedLanguage
    },
    modalPosition() {
      const windowWidth = window.innerWidth
      const windowHeight = window.innerHeight
      const modalWidth = 640
      const modalHeight = 600

      return {
        x: (windowWidth - modalWidth) / 2,
        y: (windowHeight - modalHeight) / 2,
      }
    },
    isRestrictEmbed() {
      if (this.embedSelected === 'hotmart' || this.embedSelected === 'eduzz')
        return true
      return false
    },
    embedOptions() {
      const [general] = platformsOfEmbed.filter(
        ({ value }) => value === 'general'
      )

      let rest = []
      if (this.onlyPageOptions === true) {
        rest = platformsOfEmbed.filter(
          ({ value }) => value !== 'general' && value === 'elementor'
        )
      } else {
        rest = platformsOfEmbed.filter(({ value }) => value !== 'general')
      }

      const sorted = rest.sort((a, b) => a.value.localeCompare(b.value))

      return [general, ...sorted]
    },
    textareaContent() {
      const isLive = this.$route.name === 'NewLive'

      const src = this.video.video_player ?? this.livePlayer
      const externalId = this.video.video_external_id ?? this.livePlayer
      const code = isLive
        ? this.video?.live_id || this.liveInfo.id
        : this.video.id
      const hls = isLive
        ? this.liveInfo.live_hls
        : `https://b-${this.video.pullzone_name}.tv.pandavideo.com.br/${this.video.video_external_id}/playlist.m3u8`

      if (this.hlsEmbed) return hls
      if (this.codeEmbed) return code
      if (this.srcEmbed) return src
      if (this.isExternalIdEmbed) return externalId

      return `${this.embed}${this.additionalTextAreaContent}`
    },
    textareaLabel() {
      let prefix = 'Embed'

      if (this.hlsEmbed) prefix = 'HLS'
      if (this.codeEmbed) prefix = 'Page'
      if (this.srcEmbed || this.isExternalIdEmbed) prefix = 'Source'

      return `${prefix} Code`
    },
    textareaRows() {
      let row = '5'

      if (this.hlsEmbed) row = '4'
      if (this.codeEmbed || this.srcEmbed || this.isExternalIdEmbed) row = '3'

      return row
    },
    showConfigs() {
      return (
        !this.srcEmbed &&
        !this.codeEmbed &&
        !this.hlsEmbed &&
        !this.isExternalIdEmbed
      )
    },
    showFullscreen() {
      return (
        !this.srcEmbed &&
        !this.isExternalIdEmbed &&
        !this.codeEmbed &&
        !this.hlsEmbed &&
        this.embedSelected !== 'elementor'
      )
    },
  },
  async mounted() {
    if (this.isMobileBreakpoint) {
      const crisp = document.querySelector('.cc-1m2mf')
      if (crisp) {
        crisp.style.setProperty('bottom', '80px', 'important')
      }
    }
    /* global clarity */
    clarity('set', 'EmbedModal', 'Opened')
    this.$nextTick(() => {
      if (this.type !== 'comparison') {
        const textarea = document.querySelector(
          '.v-input.wrapText.v-textarea.embedInput textarea'
        )
        if (textarea) {
          textarea.onclick = () => {
            textarea.select()
            this.handleShowLink(this.textareaContent)
          }
        }
      }
    })
    this.activeTab = this.defaultTab
    this.getUserLibrary()
    this.modalElement = document.querySelector('.embed-modal')
    this.embedSelected = ''
    let storageEmbedOption = Storage.getItem('embedType')
    if (!storageEmbedOption) {
      Storage.setItem('embedType', 'general')
      this.embedSelected = 'general'
    }
    this.embedSelected = storageEmbedOption.toLowerCase()
    this.handleEmbedSelection()

    if (this.srcEmbed) this.handleCopySrcEmbed()
    else if (this.isExternalIdEmbed) this.handleCopyExternalIdEmbed()
    else if (this.codeEmbed) this.handleCopyCodeEmbed()
    else if (this.hlsEmbed) this.handleCopyHLSEmbed()
    else this.$emit('embed', this.embed)

    this.playOpensFullscreen.iosFakeFullscreen =
      Storage.getItem('playOpensIosFakeFullscreen') ?? true
    this.playOpensFullscreen.script =
      Storage.getItem('playOpensFullscreen') ?? false
    this.playOpensFullscreen.native =
      Storage.getItem('playOpensFullscreenNative') ?? false
    this.pipScrollFollow = Storage.getItem('pipScrollFollow') ?? false
    this.emitPlayOpensState()
    if (this.type === 'comparison') {
      this.isLoading = this.fetching = false
    }
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  watch: {
    showMenuEmbed(val) {
      this.$emit('showEmbed', val)
    },
    startTimeCheckbox(value) {
      if (value === false) return

      const seconds = this.getStartTimeSeconds(this.startTime)

      if (!this.timeVideo) {
        this.startTime = this.formatTime(seconds)
        this.embedVideo.startTime = seconds
      } else if (seconds > this.timeVideo) {
        this.embedVideo.startTime = this.timeVideo
        this.startTime = this.formatTime(this.timeVideo)
      }

      this.embedVideo.startTime = seconds
    },
    embedSelected() {
      if (this.hlsEmbed) this.handleCopyHLSEmbed()
      if (this.codeEmbed) this.handleCopyCodeEmbed()
      if (this.srcEmbed) this.handleCopySrcEmbed()
      if (this.isExternalIdEmbed) this.handleCopyExternalIdEmbed()
    },
    playOpensFullscreen: {
      handler() {
        this.emitPlayOpensState()
      },
      deep: true,
    },
    preloadEmbed(value) {
      this.$emit('preloadEmbed', value)
    },
  },
  methods: {
    ...mapMutations('user', [
      'UPDATE_GLOBAL_SNACKBAR',
      'UPDATE_GLOBAL_BASE_MODAL_MESSAGE',
    ]),
    handleClickOutside() {
      this.isFocused = false
    },
    focusIcon(event) {
      event.preventDefault()
      this.isFocused = true
    },
    allowOnlyNumbers(event) {
      const charCode = event.charCode
      if (charCode < 48 || charCode > 57) {
        event.preventDefault()
      }
    },
    async getUserLibrary() {
      try {
        let ret = await referApi.getLibrary()
        this.userLibrary = ret.data
      } catch (e) {
        console.log(e)
      }
    },
    openTrialArticle() {
      window.open(this.trialArticle, '_blank')
    },
    closeModal() {
      this.showMenuEmbed = false
      if (this.isMobileBreakpoint) {
        const crisp = document.querySelector('.cc-1m2mf')
        if (crisp) {
          crisp.style.setProperty('bottom', '14px', 'important')
        }
      }
      if (this.hasChanges) {
        clarity('set', 'EmbedModal', 'ClosedWithChanges')
      }
    },
    changesInEmbed() {
      this.hasChanges = true
    },
    handleCheckClickOutside(e) {
      const { target } = e
      const embedMenuElement = document.querySelector('.embed-modal')
      const timepickerElement = document.querySelector('.ant-time-picker-panel')
      if (embedMenuElement) {
        if (target === embedMenuElement || embedMenuElement.contains(target)) {
          return false
        } else if (
          timepickerElement &&
          (timepickerElement === target || timepickerElement.contains(target))
        ) {
          return false
        } else if (this.checkParent(target)) {
          return false
        }
      }
      this.$emit('showEmbed', false)
    },
    checkParent(child) {
      let node = child.parentNode
      while (node != null) {
        if (
          node?.classList?.contains('v-menu__content') ||
          node?.classList?.contains('embedInput')
        ) {
          return true
        }
        node = node.parentNode
      }
      return false
    },
    handleChangeStartTime(time) {
      const seconds = this.getStartTimeSeconds(time)
      this.$emit('start-time', time)
      this.embedVideo.startTime = seconds
    },
    copyContent() {
      this.hasChanges = false
      if (this.activeTab === 0) {
        navigator.clipboard.writeText(this.video.video_player)
        if (this.userLibrary.block_none_refer) {
          this.UPDATE_GLOBAL_SNACKBAR({
            open: true,
            text: this.$t('message.embedModal.domainSnackBar'),
            type: 'warning',
            buttons: [
              {
                text: this.$t('message.embedModal.manageDomain'),
                size: 'small',
                style:
                  'font-size: 12px; background-color: transparent; border-color: transparent; color: #3F55EE;',
                type: 'neutral',
                icon: 'mdi-open-in-new',
                onClick: () => {
                  this.$router.push({
                    name: 'Domains',
                  })
                },
              },
            ],
          })
        } else {
          this.UPDATE_GLOBAL_SNACKBAR({
            open: true,
            text: this.$t('message.embedModal.snackBarSuccess'),
            type: 'success',
          })
        }
      } else if (this.activeTab === 1) {
        navigator.clipboard.writeText(this.textareaContent)
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: this.$t('message.embedModal.successCopy'),
          type: 'success',
        })
      } else if (this.activeTab === 2) {
        navigator.clipboard.writeText(this.textMetadata)
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: this.$t('message.embedModal.successCopyPerformance'),
          type: 'success',
        })
      } else if (this.activeTab === 3) {
        navigator.clipboard.writeText(this.video.video_hls)
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: this.$t('message.embedModal.successCopyHLS'),
          type: 'success',
        })
      }
    },
    getStartTimeSeconds(ts) {
      let time = ts.split(':')
      let seconds = +time[0] * 60 + +time[1]
      if (this.timeVideo >= 3600) {
        if (time.length === 2) {
          seconds = +time[0] * 60 + +time[1]
        } else if (time.length === 1) {
          seconds = +time[0]
        } else {
          seconds = +time[0] * 60 * 60 + +time[1] * 60 + +time[2]
        }
      } else {
        if (time.length === 1) {
          seconds = +time[0]
        }
      }
      return seconds
    },
    handleShowLink(item) {
      this.isCopied = true
      navigator.clipboard.writeText(`${item}`)
      setTimeout(() => {
        this.isCopied = false
      }, 3000)
      this.copyContent()
    },
    async readVideoInfo() {
      this.isLoading = true
      const videoId = this.videoId
      try {
        let videoInfo = await videosApi.getAVideo(videoId)
        this.video = videoInfo.data
        this.oldVideo = { ...videoInfo.data }
        if (videoInfo.data.description === null) {
          this.video.description = ''
          this.oldVideo.description = ''
        }
        this.timeVideo = this.oldVideo.length
        this.videoId = videoInfo.data.id
        this.video.video_external_id = videoInfo.data.video_external_id
        if (
          this.video.status === 'DRAFT' ||
          this.video.status === 'CONVERTING'
        ) {
          this.getStatusVideo()
        }
        this.isLoading = false
      } catch (error) {
        console.log(error)
        this.errors.videoDoesNotExist = true
        this.isLoading = false
      }
    },
    emitPlayOpensState() {
      this.$emit('playOpensFullscreen', this.playOpensFullscreen)
    },
    handleCopyHLSEmbed() {
      const hls =
        this.$route.name === 'NewLive'
          ? this.liveInfo.live_hls
          : `https://b-${this.video.pullzone_name}.tv.pandavideo.com.br/${this.video.video_external_id}/playlist.m3u8`

      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: i18n.t('message.embedHlsCopiedToClipboard'),
        type: 'success',
      })
      navigator.clipboard.writeText(`${hls}`)
    },
    handleCopyExternalIdEmbed() {
      const src = this.video.video_external_id ?? this.livePlayer
      const open = true
      const text = i18n.t('message.embedSrcCopiedToClipboard')

      this.UPDATE_GLOBAL_SNACKBAR({ open, text, type: 'success' })
      navigator.clipboard.writeText(`${src}`)
    },
    handleCopyCodeEmbed() {
      const isLive = this.$route.name === 'NewLive'
      const code = isLive
        ? this.video[isLive ? 'live_id' : 'id'] ?? this.liveInfo.id
        : this.video.id
      const open = true
      const text = i18n.t('message.embedCodeCopiedToClipboard')

      this.UPDATE_GLOBAL_SNACKBAR({ open, text, type: 'success' })
      navigator.clipboard.writeText(`${code}`)
    },
    goToDomainsPage() {
      this.$router.push({ name: 'Domains' })
    },
    handleCopySrcEmbed() {
      const src = this.video.video_player ?? this.livePlayer
      const open = true
      const text = i18n.t('message.embedSrcCopiedToClipboard')

      this.UPDATE_GLOBAL_SNACKBAR({ open, text, type: 'success' })
      navigator.clipboard.writeText(`${src}`)
    },
    async handleIosFakeFullscreen() {
      Storage.setItem(
        'playOpensIosFakeFullscreen',
        this.playOpensFullscreen.iosFakeFullscreen
      )

      this.playOpensFullscreen.native = false
      Storage.setItem(
        'playOpensFullscreenNative',
        this.playOpensFullscreen.native
      )
      this.playOpensFullscreen.script = false
      Storage.setItem('playOpensFullscreen', this.playOpensFullscreen.script)
    },
    async handleFullscreen() {
      Storage.setItem('playOpensFullscreen', this.playOpensFullscreen.script)

      this.playOpensFullscreen.native = false
      Storage.setItem(
        'playOpensFullscreenNative',
        this.playOpensFullscreen.native
      )
      this.playOpensFullscreen.iosFakeFullscreen = false
      Storage.setItem(
        'playOpensIosFakeFullscreen',
        this.playOpensFullscreen.iosFakeFullscreen
      )
    },
    async handleFullscreenNative() {
      Storage.setItem(
        'playOpensFullscreenNative',
        this.playOpensFullscreen.native
      )

      this.playOpensFullscreen.script = false
      Storage.setItem('playOpensFullscreen', this.playOpensFullscreen.script)
      this.playOpensFullscreen.iosFakeFullscreen = false
      Storage.setItem(
        'playOpensIosFakeFullscreen',
        this.playOpensFullscreen.iosFakeFullscreen
      )
    },
    async handlePipScrollFollow() {
      Storage.setItem('pipScrollFollow', this.pipScrollFollow)
    },
    handleStartCompletPlan() {
      this.$root.$emit('ModalResetBillingCycle')
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/src/_tabsV2';

.custom-tabs {
  font-size: 14px !important;
  @include v-tabs;
}

.descriptionText {
  color: varFigma(neutral-40-light);
}

.knowMoreText {
  color: varFigma(neutral-30-base);
}

.sectionTitles {
  color: varFigma(neutral-60);
}

.embedLabelOptions {
  color: varFigma(neutral-20-dark);
  font-size: 12px;
}

.embedInput ::v-deep .v-text-field__slot textarea {
  padding-right: 45px !important;
}

.actionsBtns {
  position: sticky;
  bottom: -1px;
  width: 100%;
  z-index: 10;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background: varFigma(neutral-100-white);
  border-top: 1px solid varFigma(neutral-95-lightest);
}

.v-input--selection-controls {
  margin-top: 0px;
  padding-top: 0px;
}

.appendPx {
  font-size: 12px;
  margin-top: 5px;
  color: varFigma(neutral-40-light);
}

// ::v-deep .timePicker .thumbnailTimePicker .ant-time-picker-input {
//   font-size: 12px;
//   padding: 0 8px;
//   color: varFigma(neutral-40-light);
// }

::v-deep .embed-modal {
  background: varFigma(neutral-100-white) !important;
}

::v-deep .sizeInput.base-input.v-input[data-v-36a1b07e] input {
  font-weight: 400;
  font-size: 12px !important;
  color: var(--v-neutral-40-light-base);
}

::v-deep .v-textarea textarea {
  flex: none !important;
  width: 97% !important;
}

.pandaCopy {
  color: varFigma(neutral-60);
}
</style>

<style lang="scss">
.wrapText {
  word-break: break-word;
}

.custom-tabs .v-slide-group__prev {
  display: none !important;
  visibility: hidden !important;
}

.embed-textarea textarea {
  font-weight: 400;
  font-size: 12px !important;
  color: varFigma(neutral-40-light) !important;
}

// .timePicker .thumbnailTimePicker .ant-time-picker-input {
//   font-size: 12px;
//   padding: 0 8px;
//   color: varFigma(neutral-40-light);
// }

.v-text-field.sizeInput.v-text-field--enclosed:not(.v-text-field--rounded)
  > .v-input__control
  > .v-input__slot,
.v-text-field.sizeInput.v-text-field--enclosed .v-text-field__details {
  padding: 0 8px;
}

.timePicker .ant-time-picker-icon,
.timePicker .ant-time-picker-clear {
  display: none;
}

.timePicker .thumbnailTimePicker {
  width: 52px;
}

.timePicker .darkThumbnailTimePicker {
  width: 52px;
}

.v-application--is-ltr .sizeInput.v-text-field .v-input__append-inner {
  margin-left: auto;
  padding-left: 0px;
}

.custom-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: #3f404c;
  opacity: 0.8;
  z-index: 99;
}
.center-modal {
  position: fixed;
  border: none;
  max-height: 90vh;
  border-radius: 8px;
  top: 50% !important;
  left: 50% !important  ;
  transform: translate(-50%, -50%) !important;
  z-index: 300 !important;
}
</style>
